"use client";

import { useState, useRef, useEffect, useCallback } from "react";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const REPOST_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const SUPPORT_EMAIL = "1fixfinder1@gmail.com";
const ADMIN_KEY = "fixfinder_admin_2024";

const CATEGORIES = [
  { key: "Engine", icon: "🔩", color: "#f0a500" },
  { key: "Brakes", icon: "🛑", color: "#f87171" },
  { key: "Suspension", icon: "🔧", color: "#a78bfa" },
  { key: "Cooling", icon: "❄️", color: "#34d399" },
  { key: "Exhaust", icon: "💨", color: "#fb923c" },
  { key: "Exterior", icon: "🚗", color: "#facc15" },
  { key: "Interior", icon: "🪑", color: "#e879f9" },
  { key: "Electronics", icon: "🔌", color: "#38bdf8" },
];

const BRANDS = ["Audi","Bentley","BMW","Chevrolet","Dodge","Ferrari","Ford","GMC","Honda","Hyundai","Infiniti","Isuzu","Jeep","Kia","Lamborghini","Land Rover","Lexus","Mazda","McLaren","Mercedes","Mitsubishi","Nissan","Peugeot","Porsche","Renault","Rolls-Royce","Subaru","Suzuki","Toyota","Volkswagen","Other"];
const MODELS_BY_BRAND: Record<string, string[]> = {
  Nissan: ["GTR","Patrol","Navara","Altima","Maxima","370Z","350Z","Tiida","Sentra","X-Trail"],
  Toyota: ["Supra","Land Cruiser","Hilux","Camry","Corolla","RAV4","Prado","FJ Cruiser","Yaris","Fortuner"],
  BMW: ["M3","M4","M5","X5","X6","3 Series","5 Series","7 Series","M2","M8"],
  Mercedes: ["C63","E63","G63","S Class","GLE","GLC","A Class","CLA","AMG GT","SLS"],
  Honda: ["Civic","Accord","NSX","S2000","CR-V","Pilot","HR-V","Jazz","City","Type R"],
  Audi: ["R8","RS6","RS3","A4","A5","A6","Q7","Q8","TT","S4"],
  Porsche: ["911","Cayenne","Panamera","Macan","718","Carrera","Turbo S","GT3","GT2RS"],
  Ford: ["Mustang","F-150","Raptor","Explorer","Focus","GT","Shelby","Bronco"],
  Subaru: ["WRX STI","BRZ","Impreza","Forester","Outback","Legacy","XV"],
  Mitsubishi: ["Lancer Evo","Pajero","Outlander","Eclipse","Galant","L200"],
};
const CONDITIONS = ["New", "Used", "Refurbished"];
const LOCATIONS = ["Abu Dhabi","Dubai","Sharjah","Al Ain","Ajman","RAK","UAQ","Fujairah"];
const PART_CATEGORIES = CATEGORIES.map((c) => c.key);

// ─── VEHICLE BRANDS (A–Z) ─────────────────────────────────────────────────────
const VEHICLE_BRANDS_AZ = [
  "Acura","Alfa Romeo","Aston Martin","Audi","Bentley","BMW","Bugatti","Buick",
  "Cadillac","Chevrolet","Chrysler","Citroën","Dacia","Daewoo","Dodge","Ferrari",
  "Fiat","Ford","Genesis","GMC","Honda","Hummer","Hyundai","Infiniti","Isuzu",
  "Jaguar","Jeep","Kia","Lamborghini","Land Rover","Lexus","Lincoln","Lotus",
  "Maserati","Mazda","McLaren","Mercedes","Mini","Mitsubishi","Nissan","Opel",
  "Peugeot","Pontiac","Porsche","Ram","Renault","Rolls-Royce","Saab","Saturn",
  "Seat","Skoda","Subaru","Suzuki","Tesla","Toyota","Volkswagen","Volvo","Other",
];

const SUGGESTIONS = [
  "GTR","Turbo","Exhaust","Suspension","Brakes","Engine","Intercooler","Intake","ECU","Clutch",
  "Differential","Coilover","Sway Bar","Downpipe","Boost Controller","Injectors","Fuel Pump",
  "Headers","Camshaft","Pistons","Wheels","Tires","Body Kit","Spoiler","Splitter","Diffuser",
  "Roll Cage","Bucket Seats","Harness","Steering Wheel","Radiator","Oil Cooler","Transmission",
  "Driveshaft","Axle","Blow Off Valve","Wastegate","Supercharger","Carbon Fiber","LED","Bumper",
  "Hood","Fender","Caliper","Rotor","Brake Pad","Control Arm","Strut","Shock Absorber","Spring",
  "Rim","Alloy","Intercooler Pipe","Catch Can","Air Filter","Spark Plug","Timing Belt","Alternator",
];

const FAQS = [
  { q: "How do I post a part for sale?", a: "Click '+ SELL PART' or '+ POST PART', fill in the details including photo, part name, price, and contact number, then submit. Your listing goes live instantly." },
  { q: "How long does my listing stay active?", a: "All listings automatically expire and are permanently deleted after 30 days. You'll need to repost if you still have the part available." },
  { q: "Can I repost the same listing?", a: "To prevent spam, duplicate listings are blocked for 7 days. After that cooldown period, or if you make significant changes, you can repost." },
  { q: "How do buyers contact me?", a: "Buyers contact you directly via the phone number you provide in your listing. Make sure it\'s correct before posting." },
  { q: "Is Fix Finder free to use?", a: "Yes, browsing and posting parts is completely free for all users across the UAE." },
  { q: "Can I delete my listing before it expires?", a: "Yes. Each listing you posted has a \'Delete My Ad\' button visible only to you. Click it to remove it immediately." },
  { q: "What areas does Fix Finder cover?", a: "Fix Finder covers all UAE emirates: Abu Dhabi, Dubai, Sharjah, Al Ain, Ajman, RAK, UAQ, and Fujairah." },
  { q: "I found a fake or scam listing. What do I do?", a: "Please contact our support team immediately at 1fixfinder1@gmail.com with the listing details. We will investigate and remove it." },
];

// ─── AD STATUS ────────────────────────────────────────────────────────────────
type AdStatus = "active" | "pending" | "hidden" | "expired" | "reported";

// ─── INTERFACES ───────────────────────────────────────────────────────────────
interface Listing {
  id: number;
  partName: string;
  brand: string;
  model: string;
  category: string;
  condition: string;
  location: string;
  price: string;
  contact: string;
  description: string;
  preview: string;
  images: string[];
  createdAt: number;
  ownerId: string;
  date: string;
  status: AdStatus;
}

interface FormState {
  partName: string;
  brand: string;
  model: string;
  category: string;
  condition: string;
  location: string;
  price: string;
  contact: string;
  description: string;
  images: string[];
}

interface Filters {
  brand: string;
  model: string;
  condition: string;
  location: string;
  minPrice: string;
  maxPrice: string;
}

interface SupportForm {
  name: string;
  email: string;
  message: string;
}

// ─── SESSION HELPERS ──────────────────────────────────────────────────────────
function getSessionId(): string {
  let sid = localStorage.getItem("ff_session_id");
  if (!sid) {
    sid = "u_" + Math.random().toString(36).slice(2, 10) + Date.now();
    localStorage.setItem("ff_session_id", sid);
  }
  return sid;
}

function getIsAdmin(): boolean {
  return localStorage.getItem("ff_admin_key") === ADMIN_KEY;
}

// ─── DUPLICATE DETECTION ──────────────────────────────────────────────────────
function similarity(a = "", b = ""): number {
  a = a.toLowerCase().trim();
  b = b.toLowerCase().trim();
  if (!a || !b) return 0;
  const wordsA = new Set(a.split(/\s+/));
  const wordsB = new Set(b.split(/\s+/));
  const intersection = [...wordsA].filter((w) => wordsB.has(w));
  return intersection.length / Math.max(wordsA.size, wordsB.size);
}

function isDuplicate(newItem: Partial<Listing> & { ownerId: string }, existing: Listing[]): { isDup: boolean; item?: Listing } {
  for (const item of existing) {
    if (item.ownerId !== newItem.ownerId) continue;
    const descSim = similarity(newItem.description, item.description);
    const titleSim = similarity(newItem.partName, item.partName);
    const isCooldown = Date.now() - item.createdAt < REPOST_COOLDOWN_MS;
    if ((descSim > 0.7 || titleSim > 0.8) && isCooldown) return { isDup: true, item };
  }
  return { isDup: false };
}

// ─── STORAGE HELPERS ──────────────────────────────────────────────────────────
const STORAGE_KEY = "fixfinder_listings_v2";

function loadAllListings(): Listing[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const now = Date.now();
    const parsed: Listing[] = JSON.parse(raw);
    // Migrate old listings without status
    const migrated = parsed.map((l) => ({
      ...l,
      status: l.status || ("active" as AdStatus),
    }));
    // Mark expired (30 days old) but keep them for admin
    const withExpiry = migrated.map((l) => {
      if (l.status === "active" && now - l.createdAt >= THIRTY_DAYS_MS) {
        return { ...l, status: "expired" as AdStatus };
      }
      return l;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(withExpiry));
    return withExpiry;
  } catch { return []; }
}

function loadListings(): Listing[] {
  // For regular users: only active listings
  return loadAllListings().filter((l) => l.status === "active");
}

function saveListings(list: Listing[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const inp: React.CSSProperties = {
  width: "100%",
  background: "#0b1220",
  border: "1px solid #1e2f42",
  borderRadius: 10,
  padding: "12px 14px",
  color: "#fff",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};
const sel: React.CSSProperties = { ...inp, cursor: "pointer" };

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: AdStatus }) {
  const map: Record<AdStatus, { color: string; bg: string; border: string; label: string }> = {
    active:   { color: "#4ade80", bg: "#0d2e1a", border: "#1a6b3a", label: "✅ Active" },
    pending:  { color: "#fbbf24", bg: "#1a1500", border: "#4a3800", label: "⏳ Pending" },
    hidden:   { color: "#8a9bb0", bg: "#111820", border: "#2a3a50", label: "🙈 Hidden" },
    expired:  { color: "#f87171", bg: "#1a0f0f", border: "#3a1a1a", label: "⌛ Expired" },
    reported: { color: "#fb923c", bg: "#1a0d00", border: "#4a2a00", label: "🚩 Reported" },
  };
  const s = map[status] || map.active;
  return (
    <span style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
      {s.label}
    </span>
  );
}

// ─── SUPPORT SCREEN ───────────────────────────────────────────────────────────
function SupportScreen({ onBack }: { onBack: () => void }) {
  const [form, setForm] = useState<SupportForm>({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [lastEmail, setLastEmail] = useState("");

  const set = (k: keyof SupportForm, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    const subject = encodeURIComponent(`Fix Finder Support – Message from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\nMessage:\n${form.message}\n\n---\nSent via Fix Finder Support Form`
    );
    window.open(`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`, "_blank");
    setTimeout(() => {
      setSending(false);
      setSent(true);
      setLastEmail(form.email);
      setForm({ name: "", email: "", message: "" });
    }, 800);
  };

  const required = !form.name || !form.email || !form.message;

  return (
    <div style={{ paddingTop: 20, paddingBottom: 40 }}>
      {/* Back */}
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#8a9bb0", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "0 0 20px", display: "flex", alignItems: "center", gap: 6 }}>
        ← Back to Home
      </button>

      {/* Hero */}
      <div style={{ textAlign: "center", padding: "32px 0 28px" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
        <h1 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: "clamp(28px,7vw,44px)", letterSpacing: 3, margin: "0 0 10px", color: "#fff" }}>
          CUSTOMER SUPPORT
        </h1>
        <p style={{ color: "#8a9bb0", fontSize: 15, margin: 0 }}>We&apos;re here to help. Reach out anytime.</p>
      </div>

      {/* Contact Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 }}>
        <a href={`mailto:${SUPPORT_EMAIL}`} style={{ background: "#0d1520", border: "1px solid #1e2f42", borderRadius: 14, padding: "20px 16px", textAlign: "center", textDecoration: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 28 }}>✉️</span>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Email Support</span>
          <span style={{ color: "#f0a500", fontSize: 11, wordBreak: "break-all" }}>{SUPPORT_EMAIL}</span>
        </a>
        <a href="https://wa.me/971000000000" target="_blank" rel="noopener noreferrer" style={{ background: "#0d1520", border: "1px solid #1a3a2a", borderRadius: 14, padding: "20px 16px", textAlign: "center", textDecoration: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 28 }}>📱</span>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>WhatsApp</span>
          <span style={{ color: "#4ade80", fontSize: 11 }}>Chat with us</span>
        </a>
      </div>

      {/* Contact Form */}
      <div style={{ background: "#0d1520", border: "1px solid #1e2f42", borderRadius: 16, padding: 20, marginBottom: 28 }}>
        <h2 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 20, letterSpacing: 2, color: "#f0a500", margin: "0 0 20px" }}>SEND US A MESSAGE</h2>

        {sent ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ color: "#4ade80", fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Message Sent!</div>
            <div style={{ color: "#8a9bb0", fontSize: 14, marginBottom: 6 }}>Your email client opened with the message pre-filled.</div>
            <div style={{ color: "#6a7a8a", fontSize: 13, marginBottom: 20 }}>
              We&apos;ll reply to <span style={{ color: "#f0a500" }}>{lastEmail || "your email"}</span> within 24 hours.
            </div>
            <button onClick={() => setSent(false)} style={{ background: "linear-gradient(135deg,#f0a500,#e07800)", color: "#0a0f18", border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700, fontFamily: "'Bebas Neue', cursive", letterSpacing: 1.5, cursor: "pointer" }}>
              Send Another
            </button>
          </div>
        ) : (
          <>
            {[
              { key: "name" as keyof SupportForm, label: "Your Name", placeholder: "e.g. Ahmed Al Mansouri", type: "text" },
              { key: "email" as keyof SupportForm, label: "Your Email", placeholder: "your@email.com", type: "email" },
            ].map(({ key, label, placeholder, type }) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <label style={{ display: "block", color: "#8a9bb0", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{label} *</label>
                <input type={type} value={form[key]} onChange={(e) => set(key, e.target.value)} placeholder={placeholder} style={inp} />
              </div>
            ))}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", color: "#8a9bb0", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Message *</label>
              <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} placeholder="Describe your issue or question in detail..." style={{ ...inp, resize: "none" } as React.CSSProperties} />
            </div>
            <button onClick={handleSubmit} disabled={required || sending} style={{ width: "100%", padding: "15px", borderRadius: 12, border: "none", background: required ? "#111d2e" : "linear-gradient(135deg,#f0a500,#e07800)", color: required ? "#3a4a5a" : "#0a0f18", fontSize: 16, fontWeight: 700, fontFamily: "'Bebas Neue', cursive", letterSpacing: 2, cursor: required ? "not-allowed" : "pointer" }}>
              {sending ? "OPENING EMAIL..." : "SEND MESSAGE →"}
            </button>
            <p style={{ color: "#4a5a6a", fontSize: 12, textAlign: "center", marginTop: 10, marginBottom: 0 }}>
              This will open your email client pre-filled and ready to send to {SUPPORT_EMAIL}
            </p>
          </>
        )}
      </div>

      {/* FAQ */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 20, letterSpacing: 2, color: "#fff", margin: "0 0 16px" }}>FREQUENTLY ASKED QUESTIONS</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{ background: "#0d1520", border: `1px solid ${openFaq === i ? "#f0a50040" : "#1e2f42"}`, borderRadius: 12, overflow: "hidden" }}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: "100%", background: "none", border: "none", padding: "16px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", textAlign: "left", gap: 12 }}>
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 600, flex: 1 }}>{faq.q}</span>
                <span style={{ color: "#f0a500", fontSize: 18, fontWeight: 700, flexShrink: 0, transform: openFaq === i ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}>+</span>
              </button>
              {openFaq === i && (
                <div style={{ padding: "0 18px 16px", color: "#8a9bb0", fontSize: 14, lineHeight: 1.6 }}>{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Response time */}
      <div style={{ background: "#0d1520", border: "1px solid #1e2f42", borderRadius: 14, padding: "20px 16px", textAlign: "center" }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>⏱️</div>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Average Response Time: Under 24 Hours</div>
        <div style={{ color: "#6a7a8a", fontSize: 13 }}>Our support team operates 7 days a week across UAE time zone.</div>
      </div>

      {/* Privacy Policy link */}
      <div style={{ textAlign: "center", marginTop: 24 }}>
        <a href="/privacy-policy" style={{ color: "#4a5a6a", fontSize: 13, textDecoration: "none", borderBottom: "1px solid #2a3a50", paddingBottom: 2 }}>
          🔒 Privacy Policy
        </a>
      </div>
    </div>
  );
}

// ─── LISTING CARD ─────────────────────────────────────────────────────────────
interface ListingCardProps {
  item: Listing;
  onDelete: (id: number) => void;
  sessionId: string;
  isAdmin: boolean;
}

function ListingCard({ item, onDelete, sessionId, isAdmin }: ListingCardProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const daysLeft = Math.ceil((THIRTY_DAYS_MS - (Date.now() - item.createdAt)) / 86400000);
  const canDelete = isAdmin;
  const cat = CATEGORIES.find((c) => c.key === item.category);

  // Support both old (preview only) and new (images array) listings
  const allImages: string[] = item.images && item.images.length > 0 ? item.images : item.preview ? [item.preview] : [];
  const hasMultiple = allImages.length > 1;

  const prev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIdx((i) => (i - 1 + allImages.length) % allImages.length);
  };
  const next = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIdx((i) => (i + 1) % allImages.length);
  };

  return (
    <div style={{ background: "#0f1923", border: "1px solid #1e2f42", borderRadius: 16, overflow: "hidden" }}>
      {/* Image area */}
      <div style={{ position: "relative", background: "#060c14", userSelect: "none" }}>
        {allImages.length > 0 ? (
          <img
            src={allImages[activeIdx]}
            alt={item.partName}
            style={{
              width: "100%",
              maxHeight: 320,
              objectFit: "contain",
              display: "block",
              background: "#060c14",
            }}
          />
        ) : (
          <div style={{ width: "100%", height: 200, background: "#060c14", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 40, opacity: 0.3 }}>📷</span>
          </div>
        )}

        {/* Condition / Category badges */}
        <div style={{ position: "absolute", top: 10, left: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {item.condition && (
            <span style={{ background: item.condition === "New" ? "#065f46" : "#1e3a5f", color: item.condition === "New" ? "#4ade80" : "#60a5fa", border: `1px solid ${item.condition === "New" ? "#1a7a4a" : "#2a5a8a"}`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
              {item.condition}
            </span>
          )}
          {cat && (
            <span style={{ background: "rgba(0,0,0,0.7)", color: cat.color, border: `1px solid ${cat.color}40`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
              {cat.icon} {cat.key}
            </span>
          )}
        </div>

        {/* Image count badge */}
        {hasMultiple && (
          <div style={{ position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,0.75)", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: "#fff", backdropFilter: "blur(4px)" }}>
            {activeIdx + 1} / {allImages.length}
          </div>
        )}

        {/* Price badge */}
        <div style={{ position: "absolute", bottom: hasMultiple ? 36 : 10, right: 10, background: "linear-gradient(135deg,#f0a500,#e07800)", color: "#0a0f18", borderRadius: 10, padding: "5px 12px", fontWeight: 800, fontSize: 16, fontFamily: "'Bebas Neue', cursive", letterSpacing: 1 }}>
          AED {Number(item.price).toLocaleString()}
        </div>

        {/* Prev / Next arrows */}
        {hasMultiple && (
          <>
            <button
              onClick={prev}
              style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", width: 34, height: 34, color: "#fff", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)", zIndex: 2 }}
              aria-label="Previous image"
            >‹</button>
            <button
              onClick={next}
              style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", width: 34, height: 34, color: "#fff", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)", zIndex: 2 }}
              aria-label="Next image"
            >›</button>
          </>
        )}

        {/* Dot indicators */}
        {hasMultiple && (
          <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 5 }}>
            {allImages.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setActiveIdx(i); }}
                style={{ width: i === activeIdx ? 18 : 7, height: 7, borderRadius: 4, background: i === activeIdx ? "#f0a500" : "rgba(255,255,255,0.35)", border: "none", cursor: "pointer", padding: 0, transition: "all 0.2s" }}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      <div style={{ padding: 16 }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: "#fff" }}>{item.partName}</h3>

        {(item.brand || item.model) && (
          <div style={{ color: "#8a9bb0", fontSize: 13, marginBottom: 8 }}>
            🚗 {[item.brand, item.model].filter(Boolean).join(" · ")}
          </div>
        )}

        {item.description && (
          <p style={{ color: "#6a7a8a", fontSize: 13, margin: "0 0 12px", lineHeight: 1.5 }}>{item.description}</p>
        )}

        <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          {item.location && <span style={{ color: "#8a9bb0", fontSize: 12 }}>📍 {item.location}</span>}
          <span style={{ color: "#4a5a6a", fontSize: 12 }}>⏳ {daysLeft}d left</span>
        </div>

        <a href={`tel:${item.contact}`} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "linear-gradient(135deg,#0d3a1a,#0a5020)", border: "1px solid #1a7a3a", borderRadius: 10, padding: "11px", color: "#4ade80", fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
          📞 Contact Seller
        </a>

        {canDelete && (
          <button onClick={() => onDelete(item.id)} style={{ marginTop: 10, width: "100%", padding: "10px", borderRadius: 10, border: "1px solid #3a1a1a", background: "#1a0f0f", color: "#f87171", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            🗑️ Delete Ad
          </button>
        )}
      </div>
    </div>
  );
}

// ─── ADMIN DASHBOARD ──────────────────────────────────────────────────────────
interface AdminDashboardProps {
  onBack: () => void;
}

function AdminDashboard({ onBack }: AdminDashboardProps) {
  const [allAds, setAllAds] = useState<Listing[]>([]);
  const [statusFilter, setStatusFilter] = useState<AdStatus | "all">("all");
  const [searchQ, setSearchQ] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);

  useEffect(() => {
    setAllAds(loadAllListings());
  }, []);

  const refresh = () => setAllAds(loadAllListings());

  const counts: Record<string, number> = {
    all: allAds.length,
    active: allAds.filter((a) => a.status === "active").length,
    pending: allAds.filter((a) => a.status === "pending").length,
    hidden: allAds.filter((a) => a.status === "hidden").length,
    expired: allAds.filter((a) => a.status === "expired").length,
    reported: allAds.filter((a) => a.status === "reported").length,
  };

  const filtered = allAds.filter((a) => {
    const matchStatus = statusFilter === "all" || a.status === statusFilter;
    const q = searchQ.toLowerCase();
    const matchQ = !q || [a.partName, a.brand, a.model, a.category, a.description, a.location].some((f) => f?.toLowerCase().includes(q));
    return matchStatus && matchQ;
  });

  const updateStatus = (id: number, status: AdStatus) => {
    const updated = loadAllListings().map((l) => l.id === id ? { ...l, status } : l);
    saveListings(updated);
    refresh();
  };

  const deleteAd = (id: number) => {
    const updated = loadAllListings().filter((l) => l.id !== id);
    saveListings(updated);
    setConfirmId(null);
    refresh();
  };

  const statusTabs: { key: AdStatus | "all"; label: string; color: string }[] = [
    { key: "all",      label: "All",      color: "#f0a500" },
    { key: "active",   label: "Active",   color: "#4ade80" },
    { key: "pending",  label: "Pending",  color: "#fbbf24" },
    { key: "hidden",   label: "Hidden",   color: "#8a9bb0" },
    { key: "expired",  label: "Expired",  color: "#f87171" },
    { key: "reported", label: "Reported", color: "#fb923c" },
  ];

  return (
    <div style={{ paddingTop: 20, paddingBottom: 40 }}>
      {/* Back */}
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#8a9bb0", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "0 0 16px", display: "flex", alignItems: "center", gap: 6 }}>
        ← Back to Home
      </button>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#1a0d00,#0d1520)", border: "1px solid #f0a50030", borderRadius: 16, padding: "20px 20px 16px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <span style={{ fontSize: 28 }}>🛡️</span>
          <div>
            <h1 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 26, letterSpacing: 3, color: "#f0a500", margin: 0 }}>ADMIN DASHBOARD</h1>
            <p style={{ color: "#6a7a8a", fontSize: 12, margin: 0 }}>Manage all listings · Editor access only</p>
          </div>
        </div>
        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 12 }}>
          {[
            { label: "Total", n: counts.all, color: "#f0a500" },
            { label: "Active", n: counts.active, color: "#4ade80" },
            { label: "Reported", n: counts.reported, color: "#fb923c" },
          ].map(({ label, n, color }) => (
            <div key={label} style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 22, color, letterSpacing: 1 }}>{n}</div>
              <div style={{ color: "#4a5a6a", fontSize: 10, fontWeight: 600, textTransform: "uppercase" }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 14 }}>
        <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14, pointerEvents: "none" }}>🔍</span>
        <input
          type="text"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Search ads by name, brand, location..."
          style={{ ...inp, paddingLeft: 40 }}
        />
      </div>

      {/* Status Filter Tabs */}
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8, marginBottom: 16, scrollbarWidth: "none" }}>
        {statusTabs.map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            style={{
              flexShrink: 0,
              background: statusFilter === key ? color : "#0d1520",
              color: statusFilter === key ? "#0a0f18" : color,
              border: statusFilter === key ? "none" : `1px solid ${color}40`,
              borderRadius: 20,
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            {label}
            <span style={{
              background: statusFilter === key ? "rgba(0,0,0,0.2)" : `${color}20`,
              borderRadius: 10,
              padding: "1px 6px",
              fontSize: 11,
            }}>
              {counts[key]}
            </span>
          </button>
        ))}
      </div>

      {/* Results count */}
      <div style={{ color: "#4a5a6a", fontSize: 13, marginBottom: 14 }}>
        Showing <span style={{ color: "#f0a500", fontWeight: 700 }}>{filtered.length}</span> ads
      </div>

      {/* Confirm Delete Modal */}
      {confirmId !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#0f1923", border: "1px solid #3a1a1a", borderRadius: 16, padding: 24, maxWidth: 320, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Delete this ad?</div>
            <div style={{ color: "#6a7a8a", fontSize: 13, marginBottom: 20 }}>This action cannot be undone.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button onClick={() => setConfirmId(null)} style={{ padding: "12px", borderRadius: 10, border: "1px solid #2a3a50", background: "#1a2330", color: "#8a9bb0", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => deleteAd(confirmId)} style={{ padding: "12px", borderRadius: 10, border: "none", background: "#7f1d1d", color: "#f87171", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Ads List */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", background: "#0f1923", border: "1px solid #1e2f42", borderRadius: 16 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No ads found</div>
          <div style={{ color: "#4a5a6a", fontSize: 13 }}>Try a different filter or search term</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((ad) => {
            const allImages: string[] = ad.images && ad.images.length > 0 ? ad.images : ad.preview ? [ad.preview] : [];
            const daysLeft = Math.ceil((THIRTY_DAYS_MS - (Date.now() - ad.createdAt)) / 86400000);
            return (
              <div key={ad.id} style={{ background: "#0f1923", border: "1px solid #1e2f42", borderRadius: 14, overflow: "hidden" }}>
                {/* Image + info row */}
                <div style={{ display: "flex", gap: 0 }}>
                  {allImages.length > 0 ? (
                    <img src={allImages[0]} alt={ad.partName} style={{ width: 90, height: 90, objectFit: "cover", flexShrink: 0, background: "#060c14" }} />
                  ) : (
                    <div style={{ width: 90, height: 90, background: "#060c14", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: 28, opacity: 0.3 }}>📷</span>
                    </div>
                  )}
                  <div style={{ flex: 1, padding: "10px 12px", minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                      <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{ad.partName}</div>
                      <StatusBadge status={ad.status} />
                    </div>
                    <div style={{ color: "#8a9bb0", fontSize: 12, marginBottom: 4 }}>
                      {[ad.brand, ad.model].filter(Boolean).join(" · ")} {ad.location && `· 📍${ad.location}`}
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ color: "#f0a500", fontWeight: 700, fontSize: 13 }}>AED {Number(ad.price).toLocaleString()}</span>
                      <span style={{ color: "#4a5a6a", fontSize: 12 }}>⏳ {daysLeft > 0 ? `${daysLeft}d left` : "Expired"}</span>
                      <span style={{ color: "#4a5a6a", fontSize: 12 }}>📅 {ad.date}</span>
                    </div>
                  </div>
                </div>

                {/* Admin action buttons */}
                <div style={{ padding: "10px 12px", borderTop: "1px solid #1a2535", display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {ad.status !== "active" && (
                    <button
                      onClick={() => updateStatus(ad.id, "active")}
                      style={{ flex: 1, minWidth: 80, padding: "8px 10px", borderRadius: 8, border: "1px solid #1a6b3a", background: "#0d2e1a", color: "#4ade80", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    >
                      ✅ Approve
                    </button>
                  )}
                  {ad.status !== "hidden" && (
                    <button
                      onClick={() => updateStatus(ad.id, "hidden")}
                      style={{ flex: 1, minWidth: 80, padding: "8px 10px", borderRadius: 8, border: "1px solid #2a3a50", background: "#111820", color: "#8a9bb0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    >
                      🙈 Hide
                    </button>
                  )}
                  {(ad.status === "hidden" || ad.status === "expired") && (
                    <button
                      onClick={() => updateStatus(ad.id, "active")}
                      style={{ flex: 1, minWidth: 80, padding: "8px 10px", borderRadius: 8, border: "1px solid #1e3a5f", background: "#0d1e35", color: "#60a5fa", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    >
                      🔄 Restore
                    </button>
                  )}
                  {ad.status !== "pending" && ad.status !== "active" && (
                    <button
                      onClick={() => updateStatus(ad.id, "pending")}
                      style={{ flex: 1, minWidth: 80, padding: "8px 10px", borderRadius: 8, border: "1px solid #4a3800", background: "#1a1500", color: "#fbbf24", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    >
                      ⏳ Pending
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmId(ad.id)}
                    style={{ flex: 1, minWidth: 80, padding: "8px 10px", borderRadius: 8, border: "1px solid #3a1a1a", background: "#1a0f0f", color: "#f87171", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── POST FORM ────────────────────────────────────────────────────────────────
interface PostFormProps {
  onPost: (item: Listing) => void;
  onCancel: () => void;
  sessionId: string;
}

const MAX_IMAGES = 8;

function PostForm({ onPost, onCancel, sessionId }: PostFormProps) {
  const [form, setForm] = useState<FormState>({ partName: "", brand: "", model: "", category: "", condition: "", location: "", price: "", contact: "", description: "", images: [] });
  const [error, setError] = useState("");
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [previewIdx, setPreviewIdx] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setModelOptions(MODELS_BY_BRAND[form.brand] || []);
    setForm((f) => ({ ...f, model: "" }));
  }, [form.brand]);

  const set = (k: keyof FormState, v: string | string[]) => setForm((f) => ({ ...f, [k]: v }));

  const handleImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_IMAGES - form.images.length;
    const toProcess = files.slice(0, remaining);
    let loaded = 0;
    const newImages: string[] = [];
    toProcess.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        newImages.push(ev.target?.result as string);
        loaded++;
        if (loaded === toProcess.length) {
          setForm((f) => {
            const updated = [...f.images, ...newImages];
            return { ...f, images: updated };
          });
          setPreviewIdx((idx) => idx);
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeImage = (idx: number) => {
    setForm((f) => {
      const updated = f.images.filter((_, i) => i !== idx);
      return { ...f, images: updated };
    });
    setPreviewIdx((i) => Math.max(0, i >= idx ? i - 1 : i));
  };

  const handleSubmit = () => {
    if (!form.partName || !form.price || !form.contact || form.images.length === 0) {
      setError("Please fill in: at least one photo, part name, price, and contact.");
      return;
    }
    const existing = loadAllListings();
    const { isDup, item } = isDuplicate({ ...form, preview: form.images[0], ownerId: sessionId, status: "active" }, existing);
    if (isDup && item) {
      const daysLeft = Math.ceil((REPOST_COOLDOWN_MS - (Date.now() - item.createdAt)) / 86400000);
      setError(`Duplicate listing detected. You can repost this in ${daysLeft} day(s), or edit it significantly.`);
      return;
    }
    setError("");
    onPost({
      ...form,
      preview: form.images[0],
      images: form.images,
      id: Date.now(),
      createdAt: Date.now(),
      ownerId: sessionId,
      date: new Date().toLocaleDateString("en-AE"),
      status: "active",
    });
  };

  const required = !form.partName || !form.price || !form.contact || form.images.length === 0;

  return (
    <div style={{ background: "#0d1520", border: "1px solid #1e2f42", borderRadius: 16, padding: 20, marginBottom: 20 }}>
      <h3 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 22, letterSpacing: 2, color: "#f0a500", margin: "0 0 20px" }}>POST A SPARE PART</h3>

      <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleImages} />

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", color: "#8a9bb0", fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Photos * <span style={{ color: "#4a5a6a", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>({form.images.length}/{MAX_IMAGES})</span>
        </label>

        {form.images.length > 0 ? (
          <div style={{ position: "relative", background: "#060c14", borderRadius: 12, overflow: "hidden", marginBottom: 10 }}>
            <img src={form.images[previewIdx]} alt={`Preview ${previewIdx + 1}`} style={{ width: "100%", maxHeight: 220, objectFit: "contain", display: "block", background: "#060c14" }} />
            <button onClick={() => removeImage(previewIdx)} style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.75)", border: "none", borderRadius: "50%", width: 28, height: 28, color: "#f87171", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} aria-label="Remove image">×</button>
            {form.images.length > 1 && (
              <div style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(0,0,0,0.7)", borderRadius: 20, padding: "2px 8px", fontSize: 11, color: "#fff", fontWeight: 700 }}>
                {previewIdx + 1} / {form.images.length}
              </div>
            )}
          </div>
        ) : null}

        {form.images.length > 0 && (
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, marginBottom: 10, scrollbarWidth: "none" }}>
            {form.images.map((img, i) => (
              <div key={i} onClick={() => setPreviewIdx(i)} style={{ flexShrink: 0, width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: i === previewIdx ? "2px solid #f0a500" : "2px solid #2a3a50", cursor: "pointer", background: "#060c14", position: "relative" }}>
                <img src={img} alt={`Thumb ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            ))}
            {form.images.length < MAX_IMAGES && (
              <div onClick={() => fileRef.current?.click()} style={{ flexShrink: 0, width: 64, height: 64, borderRadius: 8, border: "2px dashed #2a3a50", cursor: "pointer", background: "#0b1220", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
                <span style={{ fontSize: 20, color: "#4a5a6a" }}>+</span>
                <span style={{ fontSize: 9, color: "#4a5a6a", fontWeight: 600 }}>ADD</span>
              </div>
            )}
          </div>
        )}

        {form.images.length === 0 && (
          <div onClick={() => fileRef.current?.click()} style={{ border: "2px dashed #2a3a50", borderRadius: 12, padding: "28px 20px", textAlign: "center", cursor: "pointer" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
            <div style={{ color: "#fff", fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Tap to upload photos *</div>
            <div style={{ color: "#4a5a6a", fontSize: 12 }}>Up to {MAX_IMAGES} images · JPG, PNG, WEBP</div>
          </div>
        )}

        {form.images.length > 0 && form.images.length < MAX_IMAGES && (
          <button onClick={() => fileRef.current?.click()} style={{ width: "100%", background: "#0b1220", border: "1px dashed #2a3a50", borderRadius: 10, padding: "10px", color: "#8a9bb0", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            📷 Add More Photos ({form.images.length}/{MAX_IMAGES})
          </button>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", color: "#8a9bb0", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Part Name *</label>
        <input value={form.partName} onChange={(e) => set("partName", e.target.value)} placeholder="e.g. HKS Turbo Kit GTR R35" style={inp} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        {([
          { label: "Brand", key: "brand" as keyof FormState, options: BRANDS },
          { label: "Condition *", key: "condition" as keyof FormState, options: CONDITIONS },
          { label: "Category", key: "category" as keyof FormState, options: PART_CATEGORIES },
          { label: "Location", key: "location" as keyof FormState, options: LOCATIONS },
        ] as { label: string; key: keyof FormState; options: string[] }[]).map(({ label, key, options }) => (
          <div key={key}>
            <label style={{ display: "block", color: "#8a9bb0", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
            <select value={form[key] as string} onChange={(e) => set(key, e.target.value)} style={sel}>
              <option value="">Select</option>
              {options.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
        ))}
      </div>

      {modelOptions.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: "#8a9bb0", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Car Model</label>
          <select value={form.model} onChange={(e) => set("model", e.target.value)} style={sel}>
            <option value="">Select Model</option>
            {modelOptions.map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <div>
          <label style={{ display: "block", color: "#8a9bb0", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Price (AED) *</label>
          <input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="e.g. 1200" style={inp} />
        </div>
        <div>
          <label style={{ display: "block", color: "#8a9bb0", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Contact *</label>
          <input type="tel" value={form.contact} onChange={(e) => set("contact", e.target.value)} placeholder="+971 50 000 0000" style={inp} />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", color: "#8a9bb0", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Description</label>
        <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Condition details, compatibility, brand, year..." style={{ ...inp, resize: "none" } as React.CSSProperties} />
      </div>

      {error && <div style={{ background: "#1a0f0f", border: "1px solid #3a1a1a", borderRadius: 10, padding: "12px 14px", color: "#f87171", fontSize: 13, marginBottom: 14 }}>⚠️ {error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <button onClick={onCancel} style={{ padding: "14px", borderRadius: 12, border: "1px solid #2a3a50", background: "#1a2330", color: "#8a9bb0", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
        <button onClick={handleSubmit} disabled={required} style={{ padding: "14px", borderRadius: 12, border: "none", background: required ? "#1a2330" : "linear-gradient(135deg,#f0a500,#e07800)", color: required ? "#4a5a6a" : "#0a0f18", fontSize: 15, fontWeight: 700, fontFamily: "'Bebas Neue', cursive", letterSpacing: 2, cursor: required ? "not-allowed" : "pointer" }}>
          POST LISTING
        </button>
      </div>
    </div>
  );
}

// ─── BROWSE BY VEHICLE SCREEN ─────────────────────────────────────────────────
function BrowseByVehicleScreen({ onBack, onSelectBrand }: { onBack: () => void; onSelectBrand: (brand: string) => void }) {
  const regularBrands = VEHICLE_BRANDS_AZ.filter((b) => b !== "Other");
  const grouped: Record<string, string[]> = {};
  regularBrands.forEach((brand) => {
    const letter = brand[0].toUpperCase();
    if (!grouped[letter]) grouped[letter] = [];
    grouped[letter].push(brand);
  });
  const letters = Object.keys(grouped).sort();

  return (
    <div style={{ paddingTop: 20, paddingBottom: 40 }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#8a9bb0", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "0 0 20px", display: "flex", alignItems: "center", gap: 6 }}>
        ← Back to Home
      </button>

      <div style={{ textAlign: "center", padding: "24px 0 28px" }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>🚗</div>
        <h1 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: "clamp(28px,7vw,44px)", letterSpacing: 3, margin: "0 0 10px", color: "#fff" }}>
          BROWSE BY VEHICLE
        </h1>
        <p style={{ color: "#8a9bb0", fontSize: 14, margin: 0 }}>Select your vehicle brand to find compatible parts</p>
      </div>

      {letters.map((letter) => (
        <div key={letter} style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 22, letterSpacing: 3, color: "#f0a500", marginBottom: 10, borderBottom: "1px solid #1e2f42", paddingBottom: 6 }}>
            {letter}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
            {grouped[letter].map((brand) => (
              <button key={brand} onClick={() => onSelectBrand(brand)} style={{ background: "#0d1520", border: "1px solid #1e2f42", borderRadius: 12, padding: "14px 16px", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 10 }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.border = "1px solid #f0a50060"; (e.currentTarget as HTMLButtonElement).style.background = "#111d2e"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.border = "1px solid #1e2f42"; (e.currentTarget as HTMLButtonElement).style.background = "#0d1520"; }}
              >
                <span style={{ fontSize: 18 }}>🚘</span>
                <span>{brand}</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 22, letterSpacing: 3, color: "#8a9bb0", marginBottom: 10, borderBottom: "1px solid #1e2f42", paddingBottom: 6 }}>OTHER</div>
        <button onClick={() => onSelectBrand("Other")} style={{ width: "100%", background: "#0d1520", border: "1px solid #2a3a50", borderRadius: 12, padding: "16px", color: "#8a9bb0", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.border = "1px solid #f0a50060"; (e.currentTarget as HTMLButtonElement).style.background = "#111d2e"; (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.border = "1px solid #2a3a50"; (e.currentTarget as HTMLButtonElement).style.background = "#0d1520"; (e.currentTarget as HTMLButtonElement).style.color = "#8a9bb0"; }}
        >
          <span style={{ fontSize: 18 }}>🔍</span>
          <div>
            <div>Other / Not Listed</div>
            <div style={{ fontSize: 12, color: "#4a5a6a", fontWeight: 400, marginTop: 2 }}>My vehicle brand is not in the list above</div>
          </div>
        </button>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [sessionId, setSessionId] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [listings, setListings] = useState<Listing[]>([]);
  const [screen, setScreen] = useState("home");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [posted, setPosted] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filters, setFilters] = useState<Filters>({ brand: "", model: "", condition: "", location: "", minPrice: "", maxPrice: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [showHelp, setShowHelp] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSessionId(getSessionId());
    setIsAdmin(getIsAdmin());
    setListings(loadListings());
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setListings(loadListings());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setModelOptions(MODELS_BY_BRAND[filters.brand] || []);
    setFilters((f) => ({ ...f, model: "" }));
  }, [filters.brand]);

  const suggestions = searchQuery.length > 1
    ? [...new Set([
        ...SUGGESTIONS.filter((s) => s.toLowerCase().startsWith(searchQuery.toLowerCase())),
        ...SUGGESTIONS.filter((s) => s.toLowerCase().includes(searchQuery.toLowerCase()) && !s.toLowerCase().startsWith(searchQuery.toLowerCase())),
        ...listings.map((l) => l.partName).filter((n) => n?.toLowerCase().includes(searchQuery.toLowerCase())),
      ])].slice(0, 7)
    : [];

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  const filteredListings = listings.filter((item) => {
    const q = searchQuery.toLowerCase();
    const matchQ = !q || [item.partName, item.brand, item.model, item.category, item.description].some((f) => f?.toLowerCase().includes(q));
    const matchCat = !activeCategory || item.category === activeCategory;
    const matchBrand = !filters.brand || item.brand === filters.brand;
    const matchModel = !filters.model || item.model === filters.model;
    const matchCond = !filters.condition || item.condition === filters.condition;
    const matchLoc = !filters.location || item.location === filters.location;
    const matchMin = !filters.minPrice || Number(item.price) >= Number(filters.minPrice);
    const matchMax = !filters.maxPrice || Number(item.price) <= Number(filters.maxPrice);
    return matchQ && matchCat && matchBrand && matchModel && matchCond && matchLoc && matchMin && matchMax;
  });

  const handlePost = (newItem: Listing) => {
    const allExisting = loadAllListings();
    const updated = [newItem, ...allExisting];
    saveListings(updated);
    setListings(loadListings());
    setShowForm(false);
    setPosted(true);
    setTimeout(() => setPosted(false), 3000);
  };

  const handleDelete = (id: number) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this listing?")) return;
    const updated = loadAllListings().filter((l) => l.id !== id);
    saveListings(updated);
    setListings(loadListings());
  };

  const clearFilters = useCallback(() => setFilters({ brand: "", model: "", condition: "", location: "", minPrice: "", maxPrice: "" }), []);

  const goTo = (s: string) => {
    setScreen(s);
    setShowHelp(false);
    window.scrollTo(0, 0);
  };

  const handleSelectVehicleBrand = (brand: string) => {
    if (brand !== "Other") {
      setFilters((f) => ({ ...f, brand }));
    }
    goTo("market");
  };

  // Bottom nav items — admin tab only visible to admin
  const bottomNavItems = [
    { icon: "🏠", label: "Home", s: "home" },
    { icon: "🔩", label: "Parts", s: "market" },
    { icon: "🚗", label: "By Vehicle", s: "browse-vehicle" },
    { icon: "💬", label: "Support", s: "support" },
    ...(isAdmin ? [{ icon: "🛡️", label: "Admin", s: "admin" }] : []),
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#0a0f18", fontFamily: "'Inter', sans-serif", color: "#fff", paddingBottom: 80 }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* ── HEADER ── */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(10,15,24,0.97)", backdropFilter: "blur(12px)", borderBottom: "1px solid #1e2f42", padding: "14px 20px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div onClick={() => goTo("home")} style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 26, letterSpacing: 3, background: "linear-gradient(135deg,#f0a500,#fff)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", cursor: "pointer" }}>
            FIX FINDER
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {isAdmin && (
              <button onClick={() => goTo("admin")} style={{ background: screen === "admin" ? "rgba(240,165,0,0.15)" : "none", border: "1px solid #f0a50050", borderRadius: 8, color: "#f0a500", padding: "7px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
                🛡️ Admin
              </button>
            )}
            <button onClick={() => goTo("support")} style={{ background: "none", border: "1px solid #1e2f42", borderRadius: 8, color: "#8a9bb0", padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              Support
            </button>
            <button onClick={() => { setShowForm(true); goTo("market"); }} style={{ background: "linear-gradient(135deg,#f0a500,#e07800)", color: "#0a0f18", border: "none", borderRadius: 10, padding: "8px 16px", fontSize: 13, fontWeight: 700, fontFamily: "'Bebas Neue', cursive", letterSpacing: 1.5, cursor: "pointer" }}>
              + SELL PART
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: "0 auto", padding: "0 16px" }}>

        {/* ── HOME SCREEN ── */}
        {screen === "home" && (
          <>
            <div style={{ textAlign: "center", padding: "48px 0 32px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#0d1520", border: "1px solid #1e2f42", borderRadius: 20, padding: "6px 14px", marginBottom: 20 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#4ade80", display: "inline-block", boxShadow: "0 0 8px #4ade80" }} />
                <span style={{ color: "#8a9bb0", fontSize: 12, fontWeight: 600, letterSpacing: 1 }}>LIVE MARKETPLACE · UAE</span>
              </div>
              <h1 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: "clamp(36px,9vw,60px)", letterSpacing: 3, margin: "0 0 16px", lineHeight: 1.1 }}>
                UAE SPARE PARTS<br />MARKETPLACE
              </h1>
              <p style={{ color: "#8a9bb0", fontSize: 15, margin: "0 0 28px", lineHeight: 1.6 }}>
                Buy and sell auto parts across UAE. Fast, trusted, and direct.
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <button onClick={() => goTo("market")} style={{ background: "linear-gradient(135deg,#f0a500,#e07800)", color: "#0a0f18", border: "none", borderRadius: 14, padding: "14px 32px", fontSize: 16, fontWeight: 700, fontFamily: "'Bebas Neue', cursive", letterSpacing: 2, cursor: "pointer" }}>
                  BROWSE PARTS
                </button>
                <button onClick={() => { setShowForm(true); goTo("market"); }} style={{ background: "transparent", color: "#f0a500", border: "2px solid #f0a500", borderRadius: 14, padding: "14px 32px", fontSize: 16, fontWeight: 700, fontFamily: "'Bebas Neue', cursive", letterSpacing: 2, cursor: "pointer" }}>
                  SELL A PART
                </button>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 40 }}>
              {[
                { n: listings.length, label: "Active Listings" },
                { n: "30", label: "Day Auto-Expire" },
                { n: "UAE", label: "Wide Coverage" },
              ].map(({ n, label }) => (
                <div key={label} style={{ background: "#0d1520", border: "1px solid #1e2f42", borderRadius: 14, padding: "18px 12px", textAlign: "center" }}>
                  <div style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 28, color: "#f0a500", letterSpacing: 1 }}>{n}</div>
                  <div style={{ color: "#4a5a6a", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 40 }}>
              <h2 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 22, letterSpacing: 2, color: "#fff", margin: "0 0 16px" }}>BROWSE BY CATEGORY</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                <div onClick={() => goTo("browse-vehicle")} style={{ background: "#0d1520", border: "1px solid #60a5fa25", borderRadius: 14, padding: "18px 12px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.border = "1px solid #60a5fa60"; (e.currentTarget as HTMLDivElement).style.background = "#111d2e"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.border = "1px solid #60a5fa25"; (e.currentTarget as HTMLDivElement).style.background = "#0d1520"; }}
                >
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🚗</div>
                  <div style={{ color: "#60a5fa", fontWeight: 700, fontSize: 13 }}>Vehicle</div>
                  <div style={{ color: "#4a5a6a", fontSize: 11, marginTop: 4 }}>{listings.length} parts</div>
                </div>
                {CATEGORIES.map((cat) => (
                  <div key={cat.key} onClick={() => { setActiveCategory(cat.key); goTo("market"); }}
                    style={{ background: "#0d1520", border: `1px solid ${cat.color}25`, borderRadius: 14, padding: "18px 12px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.border = `1px solid ${cat.color}60`; (e.currentTarget as HTMLDivElement).style.background = "#111d2e"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.border = `1px solid ${cat.color}25`; (e.currentTarget as HTMLDivElement).style.background = "#0d1520"; }}
                  >
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{cat.icon}</div>
                    <div style={{ color: cat.color, fontWeight: 700, fontSize: 13 }}>{cat.key}</div>
                    <div style={{ color: "#4a5a6a", fontSize: 11, marginTop: 4 }}>{listings.filter((l) => l.category === cat.key).length} parts</div>
                  </div>
                ))}
              </div>
            </div>

            {listings.length > 0 && (
              <div style={{ marginBottom: 40 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <h2 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 22, letterSpacing: 2, color: "#fff", margin: 0 }}>RECENT LISTINGS</h2>
                  <button onClick={() => goTo("market")} style={{ background: "none", border: "none", color: "#f0a500", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>View all →</button>
                </div>
                <div style={{ display: "grid", gap: 16 }}>
                  {listings.slice(0, 3).map((item) => (
                    <ListingCard key={item.id} item={item} onDelete={handleDelete} sessionId={sessionId} isAdmin={isAdmin} />
                  ))}
                </div>
              </div>
            )}

            <div style={{ borderTop: "1px solid #1e2f42", paddingTop: 28, paddingBottom: 20, marginTop: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 20 }}>
                <div>
                  <div style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 20, letterSpacing: 2, color: "#f0a500", marginBottom: 8 }}>FIX FINDER</div>
                  <div style={{ color: "#4a5a6a", fontSize: 12, lineHeight: 1.6 }}>UAE&apos;s premier spare parts marketplace. Buy and sell auto parts with ease.</div>
                </div>
                <div>
                  <div style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 14, letterSpacing: 1.5, color: "#8a9bb0", marginBottom: 10 }}>SUPPORT</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <button onClick={() => goTo("support")} style={{ background: "none", border: "none", color: "#8a9bb0", fontSize: 13, cursor: "pointer", textAlign: "left", padding: 0 }}>Contact Us</button>
                    <button onClick={() => goTo("support")} style={{ background: "none", border: "none", color: "#8a9bb0", fontSize: 13, cursor: "pointer", textAlign: "left", padding: 0 }}>FAQ</button>
                    <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#f0a500", fontSize: 12, textDecoration: "none" }}>{SUPPORT_EMAIL}</a>
                  </div>
                </div>
              </div>
              <div style={{ color: "#2a3a50", fontSize: 11, textAlign: "center", borderTop: "1px solid #1e2f42", paddingTop: 16 }}>
                © 2025 Fix Finder UAE · All rights reserved
              </div>
            </div>
          </>
        )}

        {/* ── MARKET SCREEN ── */}
        {screen === "market" && (
          <div style={{ paddingTop: 20 }}>
            <button onClick={() => { setScreen("home"); setActiveCategory(null); setSearchQuery(""); clearFilters(); setShowForm(false); }} style={{ background: "none", border: "none", color: "#8a9bb0", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "0 0 16px", display: "flex", alignItems: "center", gap: 6 }}>
              ← Back to Home
            </button>

            {showForm && <PostForm onPost={handlePost} onCancel={() => setShowForm(false)} sessionId={sessionId} />}

            {posted && (
              <div style={{ background: "linear-gradient(135deg,#0d2e1a,#0a3d20)", border: "1px solid #1a6b3a", borderRadius: 12, padding: "16px 20px", marginBottom: 20, textAlign: "center", color: "#4ade80", fontWeight: 600, fontSize: 15 }}>
                ✅ Listing posted! It will auto-expire in 30 days.
              </div>
            )}

            <div style={{ position: "relative", marginBottom: 14 }} ref={searchRef}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 16, pointerEvents: "none" }}>🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 160)}
                placeholder="Search: GTR turbo, brake pads, M3 exhaust..."
                style={{ ...inp, paddingLeft: 42, paddingRight: searchQuery ? 40 : 14 }}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#4a5a6a", cursor: "pointer", fontSize: 20, padding: 0, lineHeight: 1 }}>×</button>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 200, background: "#0f1923", border: "1px solid #2a3a50", borderRadius: 12, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
                  <div style={{ padding: "8px 14px", borderBottom: "1px solid #1a2535" }}>
                    <span style={{ color: "#4a5a6a", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Suggestions</span>
                  </div>
                  {suggestions.map((s, i) => (
                    <div key={i} onClick={() => { setSearchQuery(s); setShowSuggestions(false); }}
                      style={{ padding: "11px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid #1a2535" }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLDivElement).style.background = "#111d2e"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLDivElement).style.background = "transparent"}
                    >
                      <span style={{ fontSize: 14 }}>🔍</span>
                      <span style={{ flex: 1, color: "#fff", fontSize: 14 }}>{s}</span>
                      <span style={{ color: "#4a5a6a", fontSize: 12 }}>→</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8, marginBottom: 14, scrollbarWidth: "none" }}>
              <button onClick={() => setActiveCategory(null)} style={{ flexShrink: 0, background: !activeCategory ? "#f0a500" : "#0d1520", color: !activeCategory ? "#0a0f18" : "#8a9bb0", border: !activeCategory ? "none" : "1px solid #2a3a50", borderRadius: 20, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                All
              </button>
              {CATEGORIES.map((cat) => (
                <button key={cat.key} onClick={() => setActiveCategory(activeCategory === cat.key ? null : cat.key)} style={{ flexShrink: 0, background: activeCategory === cat.key ? cat.color : "#0d1520", color: activeCategory === cat.key ? "#0a0f18" : cat.color, border: activeCategory === cat.key ? "none" : `1px solid ${cat.color}40`, borderRadius: 20, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  {cat.icon} {cat.key}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <button onClick={() => setShowFilters(!showFilters)} style={{ flex: 1, background: showFilters ? "#1a2535" : "#0d1520", border: showFilters ? "1px solid #f0a500" : "1px solid #2a3a50", color: showFilters ? "#f0a500" : "#8a9bb0", borderRadius: 10, padding: "11px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                ⚙️ Filters
                {activeFiltersCount > 0 && <span style={{ background: "#f0a500", color: "#0a0f18", borderRadius: "50%", width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>{activeFiltersCount}</span>}
              </button>
              {!showForm && (
                <button onClick={() => setShowForm(true)} style={{ flex: 1, background: "linear-gradient(135deg,#f0a500,#e07800)", color: "#0a0f18", border: "none", borderRadius: 10, padding: "11px", fontSize: 14, fontWeight: 700, fontFamily: "'Bebas Neue', cursive", letterSpacing: 1.5, cursor: "pointer" }}>
                  + POST PART
                </button>
              )}
            </div>

            {showFilters && (
              <div style={{ background: "#0d1520", border: "1px solid #1e2f42", borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <span style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 16, letterSpacing: 1.5, color: "#f0a500" }}>FILTER PARTS</span>
                  {activeFiltersCount > 0 && <button onClick={clearFilters} style={{ background: "none", border: "1px solid #3a1a1a", borderRadius: 8, color: "#f87171", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "4px 10px" }}>Clear All ×</button>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {([
                    { label: "Brand", key: "brand" as keyof Filters, options: BRANDS },
                    { label: "Condition", key: "condition" as keyof Filters, options: CONDITIONS },
                    { label: "Location", key: "location" as keyof Filters, options: LOCATIONS },
                  ] as { label: string; key: keyof Filters; options: string[] }[]).map(({ label, key, options }) => (
                    <div key={key}>
                      <label style={{ display: "block", color: "#8a9bb0", fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
                      <select value={filters[key]} onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))} style={sel}>
                        <option value="">All</option>
                        {options.map((o) => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                  {modelOptions.length > 0 && (
                    <div>
                      <label style={{ display: "block", color: "#8a9bb0", fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>Model</label>
                      <select value={filters.model} onChange={(e) => setFilters((f) => ({ ...f, model: e.target.value }))} style={sel}>
                        <option value="">All</option>
                        {modelOptions.map((m) => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label style={{ display: "block", color: "#8a9bb0", fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>Min Price</label>
                    <input type="number" value={filters.minPrice} onChange={(e) => setFilters((f) => ({ ...f, minPrice: e.target.value }))} placeholder="AED 0" style={inp} />
                  </div>
                  <div>
                    <label style={{ display: "block", color: "#8a9bb0", fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>Max Price</label>
                    <input type="number" value={filters.maxPrice} onChange={(e) => setFilters((f) => ({ ...f, maxPrice: e.target.value }))} placeholder="Any" style={inp} />
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ color: "#8a9bb0", fontSize: 13 }}>
                <span style={{ color: "#f0a500", fontWeight: 700 }}>{filteredListings.length}</span> parts found
                {searchQuery && <span> · &quot;<span style={{ color: "#fff" }}>{searchQuery}</span>&quot;</span>}
              </span>
              {(searchQuery || activeFiltersCount > 0 || activeCategory) && (
                <button onClick={() => { setSearchQuery(""); clearFilters(); setActiveCategory(null); }} style={{ background: "none", border: "none", color: "#f87171", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  Clear all ×
                </button>
              )}
            </div>

            {filteredListings.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 20px", background: "#0f1923", border: "1px solid #1e2f42", borderRadius: 16 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔩</div>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{searchQuery || activeFiltersCount > 0 ? "No parts match your search" : "No listings yet"}</div>
                <div style={{ color: "#4a5a6a", fontSize: 14 }}>{searchQuery ? "Try different keywords or clear filters" : "Be the first to post a part!"}</div>
              </div>
            ) : (
              <div style={{ display: "grid", gap: 16 }}>
                {filteredListings.map((item) => (
                  <ListingCard key={item.id} item={item} onDelete={handleDelete} sessionId={sessionId} isAdmin={isAdmin} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── SUPPORT SCREEN ── */}
        {screen === "support" && (
          <SupportScreen onBack={() => goTo("home")} />
        )}

        {/* ── BROWSE BY VEHICLE SCREEN ── */}
        {screen === "browse-vehicle" && (
          <BrowseByVehicleScreen onBack={() => goTo("home")} onSelectBrand={handleSelectVehicleBrand} />
        )}

        {/* ── ADMIN DASHBOARD ── (only renders if isAdmin) */}
        {screen === "admin" && isAdmin && (
          <AdminDashboard onBack={() => goTo("home")} />
        )}

        {/* Redirect non-admins away from admin screen */}
        {screen === "admin" && !isAdmin && (
          <div style={{ textAlign: "center", padding: "80px 20px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Access Denied</div>
            <div style={{ color: "#4a5a6a", fontSize: 14, marginBottom: 24 }}>You don&apos;t have permission to view this page.</div>
            <button onClick={() => goTo("home")} style={{ background: "linear-gradient(135deg,#f0a500,#e07800)", color: "#0a0f18", border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700, fontFamily: "'Bebas Neue', cursive", letterSpacing: 1.5, cursor: "pointer" }}>
              Go Home
            </button>
          </div>
        )}

      </div>

      {/* ── FLOATING HELP BUTTON ── */}
      {screen !== "support" && (
        <div style={{ position: "fixed", bottom: 90, right: 20, zIndex: 200, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          {showHelp && (
            <div style={{ background: "#0d1520", border: "1px solid #1e2f42", borderRadius: 14, padding: "16px", minWidth: 200, boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Need Help?</div>
              <button onClick={() => { goTo("support"); setShowHelp(false); }} style={{ width: "100%", background: "linear-gradient(135deg,#f0a500,#e07800)", color: "#0a0f18", border: "none", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 8, fontFamily: "'Bebas Neue', cursive", letterSpacing: 1 }}>
                💬 Contact Support
              </button>
              <a href={`mailto:${SUPPORT_EMAIL}`} style={{ display: "block", color: "#8a9bb0", fontSize: 12, textDecoration: "none", textAlign: "center" }}>✉️ {SUPPORT_EMAIL}</a>
            </div>
          )}
          <button onClick={() => setShowHelp(!showHelp)} style={{ width: 44, height: 44, borderRadius: "50%", background: showHelp ? "#1a2330" : "linear-gradient(135deg,#f0a500,#e07800)", color: showHelp ? "#8a9bb0" : "#0a0f18", border: showHelp ? "1px solid #2a3a50" : "none", fontSize: 18, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
            {showHelp ? "×" : "?"}
          </button>
        </div>
      )}

      {/* ── BOTTOM NAV ── */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(10,15,24,0.97)", backdropFilter: "blur(12px)", borderTop: "1px solid #1e2f42", display: "flex", zIndex: 100 }}>
        {bottomNavItems.map(({ icon, label, s }) => (
          <button key={s} onClick={() => { goTo(s); if (s === "home") { setActiveCategory(null); setSearchQuery(""); clearFilters(); setShowForm(false); } }} style={{ flex: 1, background: "none", border: "none", color: screen === s ? (s === "admin" ? "#f0a500" : "#f0a500") : (s === "admin" ? "#f0a50060" : "#4a5a6a"), padding: "12px 0 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{ fontSize: 22 }}>{icon}</div>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}